// Data processing utilities that replicate the Streamlit dashboard logic
// Note: 0-60 filtering is now handled in the Python script during CSV processing
import availableTournaments from '../assets/availableTournaments.json';
import graphicNameMappings from '../assets/graphicNameMappings.json';

/**
 * Count the number of rows that contain 'insights' in any column
 * Replicates the count_insights function from Streamlit dashboard
 */
export const countInsights = (data) => {
  if (!data || data.length === 0) return 0;
  
  return data.filter(row => {
    return Object.values(row).some(value => {
      const strValue = String(value).toLowerCase();
      return strValue.includes('insights') ||
             strValue.includes('in attack') ||
             strValue.includes('balanceofpower') ||
             strValue.includes('performanceratingmatchstats') ||
             strValue.includes('tennisviz overall performance rating');
    });
  }).length;
};

/**
 * Calculate the total duration of insights in seconds
 * Replicates the calculate_insights_duration function from Streamlit dashboard
 */
export const calculateInsightsDuration = (data) => {
  if (!data || data.length === 0) return 0;
  
  const insightsData = data.filter(row => 
    Object.values(row).some(value => {
      const strValue = String(value).toLowerCase();
      return strValue.includes('insights') ||
             strValue.includes('in attack') ||
             strValue.includes('balanceofpower') ||
             strValue.includes('performanceratingmatchstats') ||
             strValue.includes('tennisviz overall performance rating');
    })
  );
  
  return insightsData.reduce((sum, row) => {
    const duration = parseFloat(row.Duration_Seconds) || parseFloat(row['On Screen Duration']) || 0;
    return sum + duration;
  }, 0);
};

/**
 * Analyze and return breakdown of different insight types across all events
 * Replicates the analyze_insight_types function from Streamlit dashboard
 */
export const analyzeInsightTypes = (data) => {
  if (!data || data.length === 0) return {};
  
  // Extract insight types from Graphic Type column only
  const insightTypes = {};
  
  data.forEach(row => {
    const graphicType = String(row['Graphic Type'] || '');
    if (graphicType.toLowerCase().includes('insights') || 
        graphicType.toLowerCase().includes('in attack') ||
        graphicType.toLowerCase().includes('balanceofpower') ||
        graphicType.toLowerCase().includes('performanceratingmatchstats') ||
        graphicType.toLowerCase().includes('tennisviz overall performance rating')) {
      // Clean up the insight type name and remove "Insights " prefix
      let insightType = graphicType.trim();
      // Remove "Insights " prefix if it exists
      if (insightType.toLowerCase().startsWith('insights ')) {
        insightType = insightType.substring(9); // Remove "Insights " (9 characters)
      }
      // Normalize the insight type name to prevent duplicates
      insightType = insightType.trim();
      if (insightType) {
        insightTypes[insightType] = (insightTypes[insightType] || 0) + 1;
      }
    }
  });
  
  // Debug: Log the insight types found and their counts
  console.log('🔍 Insight types found:', Object.entries(insightTypes).map(([type, count]) => `${type}: ${count}`));
  
  return insightTypes;
};

/**
 * Format duration in a human-readable format
 * Replicates the format_duration function from Streamlit dashboard
 */
export const formatDuration = (seconds) => {
  if (seconds < 60) {
    return `${Math.round(seconds)}s`;
  } else if (seconds < 3600) {
    const minutes = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return secs > 0 ? `${minutes}m ${String(secs).padStart(2, '0')}s` : `${minutes}m`;
  } else {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.round((seconds % 3600) / 60);
    return minutes > 0 ? `${hours}h ${String(minutes).padStart(2, '0')}m` : `${hours}h`;
  }
};

/**
 * Resolve tournament type by checking the curated JSON first, then falling back to
 * the name/ID heuristic. Prefer this over getTournamentType for all new call sites.
 * @param {string|number|null} id - Tournament ID (atpId, EventId, etc.)
 * @param {string|object} nameOrRow - Tournament name or row object for heuristic fallback
 * @returns {string|null}
 */
export const resolveType = (id, nameOrRow) => {
  if (id) {
    const strId = String(id);
    for (const [type, ids] of Object.entries(availableTournaments)) {
      if (ids.includes(strId)) return type;
    }
  }
  return getTournamentType(nameOrRow);
};

/**
 * Get tournament type/category based on tournament ID
 * Maps tournament IDs to their respective categories (250, 500, 1000, NextGen, WTF)
 */
export const tournamentType_Generic = (tournamentId) => {
  let compType = "";
  switch (tournamentId) {
    case "339": case "451": case "891": case "338": case "301": case "7434": case "402": case "506": case "496": case "499": case "533":
    case "717": case "360": case "7648": case "308": case "7290": case "7163": case "322": case "7694": case "321": case "440": case "7650": case "741": case "315":
    case "439": case "316": case "6116": case "9410": case "314": case "7480": case "319": case "6242": case "568": case "341": case "6967": case "7581": case "438": case "7485":
    case "429": case "5100": case "375":
      compType = "250";
      break;
    case "407": case "6932": case "424": case "495": case "807": case "425": case "311": case "500": case "414": case "418": case "747": case "329": case "337": case "328":
      compType = "500";
      break;
    case "580": case "8096": case "404": case "403": case "8097": case "410": case "1536": case "416": case "520": case "540": case "421": case "422": case "560":
    case "8098": case "5014": case "352": case "8099": case "7161": // remove after testing add back to 250
      compType = "1000";
      break;
    case "7696": case "8000":
      compType = "NextGen";
      break;
    case "605":
      compType = "WTF";
      break;
    default:
      compType = tournamentId;
      break;
  }
  return compType;
};

/**
 * Map tournament names to their types (250, 500, 1000, etc.)
 * This uses EventId (if available) or tournament name from the data to determine the type
 * @param {string|object} tournamentNameOrRow - Tournament name string or data row object with EventId
 */
export const getTournamentType = (tournamentNameOrRow) => {
  if (!tournamentNameOrRow) return null;
  
  // If it's an object (data row), check for EventId first
  if (typeof tournamentNameOrRow === 'object' && tournamentNameOrRow.EventId) {
    const eventId = String(tournamentNameOrRow.EventId);
    const eventType = tournamentType_Generic(eventId);
    
    // Check if tournamentType_Generic returned a recognized type (not just the ID back)
    const validTypes = ['250', '500', '1000', 'NextGen', 'WTF'];
    if (validTypes.includes(eventType)) {
      return eventType;
    }
    // If EventId didn't match, fall through to name-based mapping
  }
  
  // Fall back to name-based mapping
  const tournamentName = typeof tournamentNameOrRow === 'object' 
    ? tournamentNameOrRow.Tournament 
    : tournamentNameOrRow;
  
  if (!tournamentName) return null;
  
  const name = String(tournamentName).replace(/\s/g, '').toLowerCase();
  
  // 250 Series
  const series250 = [
    'shertogenbosch', 'adelaide', 'almaty', 'antwerp', 'auckland', 'bastad', 'belgrade',
    'brisbane', 'brussels', 'bucharest', 'buenosaires', 'chengdu', 'delraybeach',
    'eastbourne', 'geneva', 'gstaad', 'hangzhou', 'hongkong', 'houston', 'kitzbuhel',
    'loscabos', 'mallorca', 'marrakech', 'marseille', 'metz', 'montpellier',
    'santiago', 'stockholm', 'stuttgart', 'umag', 'winstonsalem', 'estoril', 'newport',
    'atlanta', 'pune', 'cordoba', 'sofia', 'nur-sultan', 'antalya'
  ];
  
  // 500 Series
  const series500 = [
    'acapulco', 'barcelona', 'basel', 'beijing', 'dubai', 'halle', 'hamburg',
    'jeddah', 'london', 'queens', 'rio', 'rotterdam', 'tokyo', 'vienna', 'washington',
    'munich', 'dallas', 'doha'
  ];
  
  // 1000 Series (Masters)
  const series1000 = [
    'canada', 'cincinnati', 'indianwells', 'madrid', 'miami', 'montecarlo',
    'paris', 'rome', 'shanghai', 'toronto'
  ];
  
  // NextGen Finals
  const nextgen = ['nextgen', 'nextgenfinals'];
  
  // ATP Finals (WTF = World Tour Finals)
  const wtf = ['atpfinals', 'wtf', 'worldtourfinals', 'tourneyfinals', 'turin'];
  
  // Check which category the tournament belongs to
  if (series250.some(t => name.includes(t))) return '250';
  if (series500.some(t => name.includes(t))) return '500';
  if (series1000.some(t => name.includes(t))) return '1000';
  if (wtf.some(t => name.includes(t))) return 'WTF'; // Check ATP Finals before NextGen
  if (nextgen.some(t => name.includes(t))) return 'NextGen';
  
  return 'Other';
};

/**
 * Format tournament name by adding spaces before capital letters
 * Replicates the format_tournament_name function from Streamlit dashboard
 */
export const formatTournamentName = (tournamentName) => {
  if (!tournamentName || typeof tournamentName !== 'string') return tournamentName;
  
  // Add space before capital letters (except the first character)
  const isUppercaseLetter = (c) => c === c.toUpperCase() && c !== c.toLowerCase();
  const isLowercaseLetter = (c) => c === c.toLowerCase() && c !== c.toUpperCase();

  let formatted = '';
  for (let i = 0; i < tournamentName.length; i++) {
    const char = tournamentName[i];
    const prevChar = tournamentName[i - 1];
    if (typeof char === 'string' && typeof prevChar === 'string' &&
        isUppercaseLetter(char) && isLowercaseLetter(prevChar)) {
      formatted += ' ' + char;
    } else {
      formatted += char;
    }
  }
  
  return formatted;
};

export const transformGraphicNames = (data) => {
  if (!data || data.length === 0) return data;

  const transformations = {};

  const transformedData = data.map(row => {
    const graphicType = row['Graphic Type'];
    if (!graphicType) return row;

    const original = String(graphicType).trim();
    const target = graphicNameMappings[original.toLowerCase()];
    if (!target || original === target) return row;

    transformations[original] = target;
    return { ...row, 'Graphic Type': target };
  });

  if (Object.keys(transformations).length > 0) {
    console.log('🔍 Graphic name transformations:', transformations);
  }

  return transformedData;
};

/**
 * Filter out unwanted graphic types from the dataset
 * Replicates the filter_unwanted_graphics function from Streamlit dashboard
 */
export const filterUnwantedGraphics = (data) => {
  if (!data || data.length === 0) return data;
  
  // List of graphic types to remove (with variations)
  const unwantedGraphics = [
    'playerbioLower_Third_P6',
    'playerBioLower_Third_P6',
    'playerBioLowerThird_P6',
    'playerbioLowerThird_P6',
    'clearmapscene',
    'clearMapScene',
    'clearMapscene',
    'playerbioLower_Third_P5',
    'playerBioLower_Third_P5',
    'playerBioLowerThird_P5',
    'playerbioLowerThird_P5',
    'atprankings',
    'atp rankings',
    'atp-rankings',
    'livesinglesrankings',
    'live singles rankings',
    'live-singles-rankings',
    'resetMapScene',
    'animatetocourtxx',
    'sb_34boxes'
  ];
  
  const filteredData = data.filter(row => {
    const graphicType = String(row['Graphic Type'] || '').toLowerCase();
    const shouldKeep = !unwantedGraphics.some(unwanted => 
      graphicType === unwanted.toLowerCase()
    );
    
    // Debug: Log if we're filtering out insights
    if (!shouldKeep) {
      const hasInsights = Object.values(row).some(value => {
        const strValue = String(value).toLowerCase();
        return strValue.includes('insights') ||
               strValue.includes('in attack') ||
               strValue.includes('balanceofpower') ||
               strValue.includes('performanceratingmatchstats') ||
               strValue.includes('tennisviz overall performance rating');
      });
      if (hasInsights) {
        console.warn(`⚠️ Filtering out row with insights: ${graphicType}`);
      }
    }
    
    return shouldKeep;
  });
  
  console.log(`  🔍 filterUnwantedGraphics: filtered out ${data.length - filteredData.length} rows`);
  
  return filteredData;
};

/**
 * Process data to match the Streamlit dashboard workflow
 * This is the main function that applies all the data processing steps
 */
export const processDataForDashboard = (data) => {
  if (!data || data.length === 0) return data;
  
  console.log('🔍 Data processing debug:');
  console.log(`  📊 Input data rows: ${data.length}`);
  
  // Apply transformations in the same order as Streamlit dashboard
  let processedData = transformGraphicNames(data);
  console.log(`  📊 After transformGraphicNames: ${processedData.length} rows`);
  
  processedData = filterUnwantedGraphics(processedData);
  console.log(`  📊 After filterUnwantedGraphics: ${processedData.length} rows`);
  
  // Count insights before and after processing
  const insightsBefore = data.filter(row => 
    Object.values(row).some(value => {
      const strValue = String(value).toLowerCase();
      return strValue.includes('insights') ||
             strValue.includes('in attack') ||
             strValue.includes('balanceofpower') ||
             strValue.includes('performanceratingmatchstats') ||
             strValue.includes('tennisviz overall performance rating');
    })
  ).length;
  const insightsAfter = processedData.filter(row => 
    Object.values(row).some(value => {
      const strValue = String(value).toLowerCase();
      return strValue.includes('insights') ||
             strValue.includes('in attack') ||
             strValue.includes('balanceofpower') ||
             strValue.includes('performanceratingmatchstats') ||
             strValue.includes('tennisviz overall performance rating');
    })
  ).length;
  
  console.log(`  💡 Insights before processing: ${insightsBefore}`);
  console.log(`  💡 Insights after processing: ${insightsAfter}`);
  
  return processedData;
};

/**
 * Calculate all key metrics for the dashboard
 * Replicates the exact calculation logic from Streamlit dashboard
 */
export const calculateDashboardMetrics = (data, tournaments) => {
  if (!data || data.length === 0) {
    return {
      totalTournaments: 0,
      totalDuration: 0,
      totalSponsors: 0,
      totalGraphics: 0,
      totalInsights: 0,
      tournamentLogoDuration: 0,
      insightsDuration: 0,
      pifDuration: 0,
      lexusDuration: 0,
      rolexDuration: 0,
      top10Graphics: {},
      insightTypes: {},
      top5Events: {}
    };
  }
  
  try {
    // Process data first
    const processedData = processDataForDashboard(data);

    const filteredData = processedData.filter(row => {
      const duration = typeof row.Duration_Seconds === 'number' ? row.Duration_Seconds : parseFloat(row.Duration_Seconds);
      const onScreen = typeof row['On Screen Duration'] === 'number' ? row['On Screen Duration'] : parseFloat(row['On Screen Duration']);

      const durationOk = isNaN(duration) || duration <= 60;
      const onScreenOk = isNaN(onScreen) || onScreen <= 60;
      return durationOk && onScreenOk;
    });


    const tournamentNames = filteredData.map(row => row.Tournament).filter(Boolean);
    const uniqueTournaments = [...new Set(tournamentNames)];

    // Debug logging
    console.log('Tournament counting debug:');
    console.log('Total rows with tournament names:', tournamentNames.length);
    console.log('Unique tournament names:', uniqueTournaments);
    console.log('Unique tournament count:', uniqueTournaments.length);

    const totalTournaments = uniqueTournaments.length;
    const totalDuration = filteredData.reduce((sum, row) => {
      const duration = parseFloat(row.Duration_Seconds) || parseFloat(row['On Screen Duration']) || 0;
      return sum + duration;
    }, 0);

    // Clean and normalize sponsor names to handle variations
    const normalizedSponsors = filteredData
      .map(row => String(row.Sponsor || '').trim().toLowerCase().replace(/\s+/g, ' '))
      .filter(sponsor => sponsor && sponsor !== 'nan' && sponsor !== '');
    
    //  const totalSponsors = new Set(normalizedSponsors).size; // to revert
    const totalSponsors = 5; //Temporary hardcoded value

    const totalGraphics = new Set(filteredData.map(row => row['Graphic Type']).filter(Boolean)).size;

    // Insights should only be counted when both IsOnAir and IsVisible are true
    const countableData = filteredData.filter(row => {
      const isOnAir = row.IsOnAir === true || row.IsOnAir === 'true';
      const isVisible = row.IsVisible === true || row.IsVisible === 'true';
      return isOnAir && isVisible;
    });

    const totalInsights = countInsights(countableData);
    const insightsDuration = calculateInsightsDuration(countableData);

    // Get top 10 graphics by duration
    const graphicsByDuration = {};
    filteredData.forEach(row => {
      const graphicType = row['Graphic Type'];
      const duration = parseFloat(row.Duration_Seconds) || parseFloat(row['On Screen Duration']) || 0;
      if (graphicType && duration > 0) {
        graphicsByDuration[graphicType] = (graphicsByDuration[graphicType] || 0) + duration;
      }
    });

    const top10Graphics = Object.entries(graphicsByDuration)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 10)
      .reduce((obj, [key, value]) => {
        obj[key] = value;
        return obj;
      }, {});

      const sponsorData = {};

      //const lexusSponsorTournaments = ['301', '375', '407', '717', '425', '414', '311', '741', '319', '9410', '352']
      console.log("Tournamnets In Data Processor")
      console.log(tournaments)
    filteredData.forEach(row => {
      const sponsor = String(row.Sponsor || '').trim().toLowerCase().replace(/\s+/g, ' ');
        
      const graphicType = String(row['Graphic Type'] || '').trim().toLowerCase().replace(/\s+/g, ' ');
      const eventTemplate = String(row.EventTemplate || '').trim().toLowerCase();
      const duration = parseFloat(row.Duration_Seconds) || parseFloat(row['On Screen Duration']) || 0;
      const isOnAir = row.IsOnAir === true || row.IsOnAir === 'true';
      const isVisible = row.IsVisible === true || row.IsVisible === 'true';

      const shouldCount = isOnAir;

      // Sponsors that REQUIRE visibility/on-air
      // 'tournamentsponsor' is handled separately below to avoid double-counting with the tournament logo check
      const gatedSponsors = ['rolex', 'pif', 'lexus'];

      if (sponsor && sponsor !== 'nan' && sponsor !== 'tournamentsponsor') {
        if (gatedSponsors.includes(sponsor)) {
          if (isOnAir && isVisible) {
              if (sponsor == 'lexus') {
                  //const tournamentId = String(row.EventId || '').trim();
                  //if (!lexusSponsorTournaments.includes(tournamentId)) {
                      //console.warn(`Skipping Lexus sponsor for tournament ID ${tournamentId} as it's not in the allowed list.`);
                  //    return; // Skip this row for Lexus
                  //}
                  //console.log(`Adding Lexus sponsor for tournament ID ${tournamentId} as it's in the allowed list.`)
              }
              sponsorData[sponsor] = (sponsorData[sponsor] || 0) + duration;              
          }
        } else {
          sponsorData[sponsor] = (sponsorData[sponsor] || 0) + duration;
        }
      }
      
      if (
        graphicType.includes('tournament logo') ||
        graphicType === 'tournament logo' ||
        sponsor.includes('tournamentsponsor')
      ) {
        if (shouldCount) {
          sponsorData['tournamentsponsor'] = (sponsorData['tournamentsponsor'] || 0) + duration;
        }
      }
    });

    const tournamentLogoDuration = sponsorData['tournamentsponsor'] || 0;
    const pifDuration = sponsorData['pif'] || 0;
    const lexusDuration = sponsorData['lexus'] || 0;
    const rolexDuration = sponsorData['rolex'] || 0;

    // Debug logging for duration calculation
    console.log('Duration calculation debug:');
    console.log('Total rows processed:', filteredData.length);
    console.log('Rows with Duration_Seconds:', filteredData.filter(row => row.Duration_Seconds).length);
    console.log('Rows with On Screen Duration:', filteredData.filter(row => row['On Screen Duration']).length);
    console.log('Rows with no duration data:', filteredData.filter(row => !row.Duration_Seconds && !row['On Screen Duration']).length);
    console.log('Total duration calculated (hours):', totalDuration/3600);
    console.log('Sponsor data keys:', Object.keys(sponsorData));
    console.log('Tournament logo duration:', tournamentLogoDuration);
    console.log('PIF duration:', pifDuration);
    console.log('Lexus duration:', lexusDuration);
    console.log('Rolex duration:', rolexDuration);

    // Analyze insight types
    const insightTypes = analyzeInsightTypes(countableData);

    // Top 5 events by insights count
    const eventsByInsights = {};
    countableData.forEach(row => {
        const tournament = row.Tournament;
        const tournamentId = row.EventId;
        const tournamentName = tournaments.find(t => String(t.id) === String(tournamentId))?.city || tournament;
      const hasInsights = Object.values(row).some(value => {
        const strValue = String(value).toLowerCase();
        return strValue.includes('insights') ||
               strValue.includes('in attack') ||
               strValue.includes('balanceofpower') ||
               strValue.includes('performanceratingmatchstats') ||
               strValue.includes('tennisviz overall performance rating');
      });
        if (hasInsights && tournamentName) {
            eventsByInsights[tournamentName] = (eventsByInsights[tournamentName] || 0) + 1;
      }
    });

    const top5Events = Object.entries(eventsByInsights)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 5)
      .reduce((obj, [key, value]) => {
        try {
          obj[formatTournamentName(key)] = value;
        } catch (error) {
          console.warn('Error formatting tournament name:', key, error);
          obj[key] = value; // Use original name if formatting fails
        }
        return obj;
      }, {});

    return {
      totalTournaments,
      totalDuration,
      totalSponsors,
      totalGraphics,
      totalInsights,
      tournamentLogoDuration,
      insightsDuration,
      pifDuration,
      lexusDuration,
      rolexDuration,
      top10Graphics,
      insightTypes,
      top5Events
    };
  } catch (error) {
    console.error('Error calculating dashboard metrics:', error);
    // Return default values if calculation fails
    return {
      totalTournaments: 0,
      totalDuration: 0,
      totalSponsors: 0,
      totalGraphics: 0,
      totalInsights: 0,
      tournamentLogoDuration: 0,
      insightsDuration: 0,
      pifDuration: 0,
      lexusDuration: 0,
      rolexDuration: 0,
      top10Graphics: {},
      insightTypes: {},
      top5Events: {}
    };
  }
}; 